import React, { useEffect, useMemo, useState } from "react";
import {
  FiSave,
  FiChevronDown,
  FiTrash2,
} from "react-icons/fi";
import { FiEdit2,FiCheck } from "react-icons/fi";
import { Trash2 } from "lucide-react";
import SalesCustomerPicker from "./pharmacymodels/SalesCustomerPicker";
import CustomerCreateModal from "./pharmacymodels/CustomerCreateModal";
import { DatePicker } from "rsuite";
import ProductTableModal from "./ProductTableModal";
import { Message, useToaster, Stack } from "rsuite";
import { useLocation } from "react-router-dom";
import PatientSelectionModal from "./PatientSelectionModal";




/* Small primitives */
const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-xs font-medium text-gray-600 mb-1">
    {children}
  </label>
);


const Field = React.forwardRef(
  ({ left, right, id, className = "", type = "text", onKeyDown, ...props }, ref) => {
    // Handle numeric validation for money/number fields
    const handleKeyDown = (e) => {
      if (type === "number" || (left && left.type?.name === "RupeeSymbol")) {
        // Allow: backspace, delete, tab, escape, enter, period, minus
        if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true) ||
            // Allow: home, end, left, right, down, up
            (e.keyCode >= 35 && e.keyCode <= 40)) {
          return;
        }
        // Allow minus only at the beginning
        if (e.keyCode === 189 && e.target.value === "") {
          return;
        }
        // Ensure it's a number and stop the keypress
        if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
          e.preventDefault();
        }
      }
      // Call custom onKeyDown if provided
      if (onKeyDown) onKeyDown(e);
    };
 


    return (
      <div className={`relative ${className}`}>
        {left ? <div className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">{left}</div> : null}
        <input
          ref={ref}
          id={id}
          type={type}
          onKeyDown={handleKeyDown}
          className={`w-full h-10 rounded-md border border-gray-200 bg-white text-sm text-gray-700 placeholder-gray-400
          focus:ring-2 focus:ring-sky-300 focus:border-sky-300 outline-none transition
          ${left ? "pl-8" : "pl-3"} ${right ? "pr-8" : "pr-3"}`}
          {...props}
        />
        {right ? <div className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">{right}</div> : null}
      </div>
    );
  }
);
Field.displayName = "Field";

const Button = ({ variant = "ghost", children, className = "", ...props }) => {
  const base =
    "h-10 inline-flex items-center gap-2 px-3 rounded-md text-sm transition border";
  const styles = {
    ghost:
      "bg-white border-gray-200 text-gray-700 hover:bg-gray-50 active:bg-gray-100",
    primary:
      "bg-sky-600 border-sky-600 text-white hover:bg-sky-700 active:bg-sky-800",
    danger:
      "bg-white border-red-200 text-red-600 hover:bg-red-50 active:bg-red-100",
  };
  return (
    <button className={`${base} ${styles[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

// Rupee symbol component for type checking
const RupeeSymbol = () => <span className="text-gray-500">₹</span>;

const emptyRow = (i) => ({
  key: crypto.randomUUID(),
  index: i + 1,
  product: "",
  invoiceNo: "",
  invoiceDate: "",
  batch: "",
  hsn: "",
  pack: "1 x",
  expiry: "",
  mrp: "",
  purcRate: "",
  saleRate: "",
  returnNumber: "", // added/ensured
  discPct: "",
  gstPct: "",
  isEditable: true, 
});

const toNum = (v, d = 0) => Number(v ?? d) || d;

function calcAmount(row) {
  const rate = toNum(row.saleRate);
  const qty = toNum(row.returnNumber);
  const gross = rate * qty;
  const disc = gross * (toNum(row.discPct) / 100);
  const afterDisc = gross - disc;
  const gst = afterDisc * (toNum(row.gstPct) / 100);
  const amount = afterDisc + gst;
  return Math.max(0, Number(amount.toFixed(2)));
}

// Input validation functions
const validateNumericInput = (value, allowDecimal = true) => {
  if (value === "") return "";
  const regex = allowDecimal ? /^\d*\.?\d*$/ : /^\d*$/;
  return regex.test(value) ? value : value.slice(0, -1);
};

const validatePercentage = (value) => {
  const numValue = Number(value);
  if (isNaN(numValue) || numValue < 0) return "";
  if (numValue > 100) return "100";
  return value;
};

export default function AddSaleReturn() {
  const [returnNo, setReturnNo] = useState("CreditNote/1");
  const [returnDate, setReturnDate] = useState("");
  const [customer, setCustomer] = useState("");
  const [customerOpen, setCustomerOpen] = useState(false);
  const [rows, setRows] = useState([emptyRow(0)]);
  const [showSettings, setShowSettings] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [patientModal, setPatientModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

const location = useLocation();
const editData = location.state;
const isEdit = editData?.id ? true : false;
const totalAmount = useMemo(
  () => rows.reduce((sum, r) => sum + calcAmount(r), 0).toFixed(2),
  [rows]
);

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "F2") {
        e.preventDefault();
        setShowSettings((s) => !s);
      }
      if (e.altKey && (e.code === "KeyS" || e.key.toLowerCase() === "s")) {
        e.preventDefault();
        handleSave();
      }
      if (
        document.activeElement?.id === "customer" &&
        e.code === "Space" &&
        !customerOpen
      ) {
        e.preventDefault();
        setCustomerOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [customerOpen]);

  useEffect(() => {
  if (validationMessage) {
    const timer = setTimeout(() => setValidationMessage(""), 3000);
    return () => clearTimeout(timer);
  }
}, [validationMessage]);
const handleAddRow = () => {
  const firstRow = rows[0];

  // Validate before adding new row
  if (
    !firstRow.product ||
    !firstRow.invoiceNo ||
    !firstRow.invoiceDate ||
    !firstRow.saleRate ||
    !firstRow.returnNumber
  ) {
    setValidationMessage("Please fill all mandatory fields before adding a new row.");
    return;
  }

  // Lock filled rows
  const lockedRows = rows.map((row) => ({
    ...row,
    isEditable: false
  }));

  // Create a new empty editable row
  const newRow = emptyRow(rows.length);
  newRow.isEditable = true;

  // Update rows
  setRows([newRow, ...lockedRows]);
  setValidationMessage("");

  // Focus on first field of new row
  setTimeout(() => {
    const firstInput = document.querySelector("input[placeholder='Search / Scan barcode']");
    if (firstInput) firstInput.focus();
  }, 100);
};

  
  const handleDeleteRow = (key) => {
    if (rows.length > 1) { // Keep at least one row
      setRows((r) => {
        const filtered = r.filter(x => x.key !== key);
        // Re-index rows after deletion
        return filtered.map((row, index) => ({ ...row, index: index + 1 }));
      });
    }
  };
  const handleToggleEdit = (key) => {
  setRows((r) =>
    r.map((row) =>
      row.key === key ? { ...row, isEditable: !row.isEditable } : row
    )
  );
};


  const updateRow = (key, patch) => {
    setRows((r) => r.map((x) => {
      if (x.key === key) {
        const updatedPatch = { ...patch };
        
        // Validate numeric fields with rupee symbol
        if ('mrp' in patch) updatedPatch.mrp = validateNumericInput(patch.mrp);
        if ('purcRate' in patch) updatedPatch.purcRate = validateNumericInput(patch.purcRate);
        if ('saleRate' in patch) updatedPatch.saleRate = validateNumericInput(patch.saleRate);
        
        // Validate integer fields
        if ("returnNumber" in patch) {
  updatedPatch.returnNumber = validateNumericInput(patch.returnNumber, false);
}
        
        // Validate percentage fields
        if ('discPct' in patch) updatedPatch.discPct = validatePercentage(validateNumericInput(patch.discPct));
        if ('gstPct' in patch) updatedPatch.gstPct = validatePercentage(validateNumericInput(patch.gstPct));
        
        return { ...x, ...updatedPatch };
      }
      return x;
    }));
  };

  const handleSave = () => {
    const payload = {
      returnNo,
      returnDate,
      customer,
      items: rows.map((r) => ({ ...r, amount: calcAmount(r) })),
      total: Number(totalAmount),
    };
    console.log("SAVE", payload);
    alert("Sales Return saved!");
  };

    const [showCustomerPicker, setShowCustomerPicker] = useState(false);
     const [customers, setCustomers] = useState([
      { id: 1, name: 'Sample Customer', address: 'Sector 37, Gurgaon, Haryana', mobile: '9999888877', type: 'Customer', nature: 'Individual', discount: '-', balance: 359, balanceLabel: '₹359' },
      { id: 2, name: 'Cash Sale', address: '', mobile: '-', type: 'Customer', nature: 'Individual', discount: '85%', balance: 20, balanceLabel: '₹20' },
    ]); 
      
    const sampleRows = [
      {
        id: 1,
        name: "Dolo 650mg Tablet",
        brand: "Centurion Lab",
        verified: true,
        status: "In Stock",
        location: "B29 ALU",
        mrp: 124.5,
        batch: "1",
        stockPacks: 19,
        loose: 0,
        expiry: "10/28",
      },
       {
        id: 2,
        name: "Acetaminophen 500mg Capsule",
        brand: "MediHealth",
        verified: true,
        status: " In Stock",
        location: "B12 BLU",
        mrp:  89.0,
        batch: "2",
        stockPacks: 26,
        loose: 0,
        expiry: "12/29",
      },
    ];
    
    
    const [showCreateCustomer, setShowCreateCustomer] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

      const [openProductModal, setOpenProductModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [activeRowKey, setActiveRowKey] = useState(null);

  const toaster = useToaster();
    
     const handleSelect = (row) => {
  if (activeRowKey) {
    updateRow(activeRowKey, {
      product: row.name,
      batch: row.batch,
      expiry: row.expiry,
      mrp: row.mrp,
      purcRate: row.mrp,
      saleRate: row.mrp,
    });
  }

  setSelected(row);
  setActiveRowKey(null);       // clear after use
  setOpenProductModal(false);  // close modal

  toaster.push(
    <Message type="success" closable>
      Selected: {row.name}
    </Message>,
    { placement: "topEnd", duration: 2000 }
  );
};


  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="w-full">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
  {isEdit ? "Edit Sale Return" : "Add Sale Return"}
</h1>

              <p className="text-xs text-gray-500">Fill in the details below to add a new return</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button className="gradient-button flex items-center gap-2" onClick={handleSave}>
              <FiSave className="w-4 h-4" />
              <span>Save</span>
            </button>
          </div>
        </div>

        <div className="bg-white p-3 rounded-xl shadow-sm mb-5">
          <div className="grid grid-cols-12 gap-4 p-4">
            <div className="col-span-12 md:col-span-3">
              <Label htmlFor="returnNo">RETURN NO</Label>
              <Field
                id="returnNo"
                value={returnNo}
                onChange={(e) => setReturnNo(e.target.value)}
                placeholder="CreditNote/1"
              />
            </div>

            <div className="col-span-12 md:col-span-3">
              <Label htmlFor="returnDate">RETURN DATE</Label>
              <DatePicker
                            name="dateofbirth"
                            value={returnDate}
                            onChange={(value) => {console.log(value); setReturnDate(value);}}
                            format="dd-MMM-yyyy"
                            placeholder="dd-mmm-yyyy"
                            oneTap
                            style={{ width: "100%", height: "33px", borderRadius: "4px" }}
                          />
            </div>

            <div className="col-span-12 md:col-span-6">
              <div className="flex items-center justify-between">
                <Label htmlFor="customer">PATIENT NAME*</Label>
              </div>
              <div className="relative">
                <Field
  id="customer"
  value={selectedPatient?.patientName || ""}
  readOnly
  onClick={() => setPatientModal(true)}
  placeholder="Type here..."
  right={<FiChevronDown className="w-4 h-4" />}
  onFocus={() => setCustomerOpen(false)}
/>

                {customerOpen && (
                  <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow">
                    {["Walk-in", "ABC Pharmacy", "City Clinic"].map((opt) => (
                      <div
                        key={opt}
                        onMouseDown={() => {
                          setCustomer(opt);
                          setCustomerOpen(false);
                        }}
                        className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        {opt}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-white shadow-sm">
          

          <div className="border-t border-gray-100" />
          <div className="overflow-x-auto">
            <div className="min-w-[1300px]">
              <div className="grid grid-cols-[50px_minmax(260px,1fr)_140px_150px_120px_100px_90px_120px_110px_110px_110px_90px_100px_100px_120px_45px_40px] gap-3 px-4 py-3 text-[12px] font-bold text-gray-600">
                <div className="flex items-center">#</div>
                <div className="flex items-center">PRODUCT NAME</div>
                <div className="flex items-center">INVOICE NO*</div>
                <div className="flex items-center">INVOICE DATE*</div>
                <div className="flex items-center">BATCH</div>
                <div className="flex items-center">HSN</div>
                <div className="flex items-center">PACK</div>
                <div className="flex items-center">EXPIRY</div>
                <div className="flex items-center">MRP</div>
                <div className="flex items-center">PURC RATE</div>
                <div className="flex items-center">SALE RATE</div>
                <div className="flex items-center">RETURN</div>
                <div className="flex items-center">DISC %</div>
                <div className="flex items-center">GST %</div>
                <div className="flex items-center">AMOUNT</div>
                <div className="flex items-center">
                    <button className="gradient-button" onClick={handleAddRow}>Add</button>
                </div>
                <div></div>
                <div></div>
              </div>
{validationMessage && (
  <div className="px-4 pb-2 flex justify-end">
    <span className="text-sm text-red-500 font-medium">
      {validationMessage}
    </span>
  </div>
)}


              {rows.map((r) => (
                <div
                  key={r.key}
                  className="grid grid-cols-[50px_minmax(260px,1fr)_140px_150px_120px_100px_90px_120px_110px_110px_110px_90px__100px_100px_120px_45px_40px] gap-3 px-4 pb-4"
                >
                  <div className="flex items-center text-sm text-gray-700">{r.index}</div>

                    <Field
                    placeholder="Search / Scan barcode"
                    value={r.product}
                    onClick={() => {
                      setActiveRowKey(r.key);   
                      setOpenProductModal(true);
                    }}
                    onChange={(e) => updateRow(r.key, { product: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <Field
                    placeholder="Search/Add"
                    value={r.invoiceNo}
                    onChange={(e) => updateRow(r.key, { invoiceNo: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <DatePicker
                  name="dateofbirth"
                  value={r.invoiceDate}
                  onChange={(value) => updateRow(r.key, { invoiceDate: value })}
                  format="dd-MMM-yyyy"
                  placeholder="dd-mmm-yyyy"
                  oneTap
                  style={{ width: "100%", height: "33px", borderRadius: "4px" }}
                    disabled={!r.isEditable}
                  />

                  <Field
                    placeholder="Batch"
                    value={r.batch}
                    onChange={(e) => updateRow(r.key, { batch: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <Field
                    placeholder="HSN"
                    value={r.hsn}
                    onChange={(e) => updateRow(r.key, { hsn: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <Field
                    placeholder="1 x"
                    value={r.pack}
                    onChange={(e) => updateRow(r.key, { pack: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <Field
                    placeholder="MM/YY"
                    value={r.expiry}
                    onChange={(e) => updateRow(r.key, { expiry: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <Field
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    left={<RupeeSymbol />}
                    value={r.mrp}
                    onChange={(e) => updateRow(r.key, { mrp: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <Field
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    left={<RupeeSymbol />}
                    value={r.purcRate}
                    onChange={(e) => updateRow(r.key, { purcRate: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <Field
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    left={<RupeeSymbol />}
                    value={r.saleRate}
                    onChange={(e) => updateRow(r.key, { saleRate: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <Field
  type="number"
  step={1}
  placeholder="0"
  value={r.returnNumber}
  onChange={(e) => updateRow(r.key, { returnNumber: e.target.value })}
  readOnly={!r.isEditable}
/>

                  <Field
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={r.discPct}
                    onChange={(e) => updateRow(r.key, { discPct: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <Field
                    type="number"
                    step="0.01"
                    placeholder="0"
                    value={r.gstPct}
                    onChange={(e) => updateRow(r.key, { gstPct: e.target.value })}
                    readOnly={!r.isEditable}
                  />

                  <Field readOnly value={calcAmount(r).toFixed(2)} left={<RupeeSymbol />} className="pointer-events-none bg-gray-50" />


<div className="flex items-center gap-2">
  {r.product && r.invoiceNo && r.invoiceDate && r.saleRate && r.returnNumber ? (
    <>
    <button
  onClick={() => handleToggleEdit(r.key)}
  className="p-1 min-w-0 text-blue-600 hover:text-blue-800"
  title={r.isEditable ? "Save Row" : "Edit Row"}
>
  {r.isEditable ? (
    <FiCheck className="w-4 h-4 text-green-600 hover:text-green-700" />
  ) : (
    <FiEdit2 className="w-4 h-4" />
  )}
</button>



      {rows.length > 1 && (
        <button
          variant="danger"
          onClick={() => handleDeleteRow(r.key)}
          className="p-1 min-w-0 text-red-600 hover:text-red-800"
          title="Delete Row"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </>
  ) : null}
</div>


                </div>
              ))}
              
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 border-t border-gray-100 px-4 py-3">
            <div className="text-sm text-gray-600">Total Amount</div>
            <div className="min-w-[140px]">
              <Field readOnly left={<RupeeSymbol />} value={totalAmount} className="bg-gray-50" />
            </div>
          </div>
        </div>
      </div>

            {/* <SalesCustomerPicker
        show={showCustomerPicker}
        onHide={() => setShowCustomerPicker(false)}
        customers={customers}
        onCreateNew={() => { setShowCustomerPicker(false); setShowCreateCustomer(true); }}
        onSelect={(c) => {
          setSelectedCustomer(c);
          setShowCustomerPicker(false);
        }}
      />
      <CustomerCreateModal
        show={showCreateCustomer}
        onHide={() => setShowCreateCustomer(false)}
        onSubmit={(newCust) => {
          setCustomers(prev => [newCust, ...prev]);          
          setSelectedCustomer(newCust);                      
          setShowCreateCustomer(false);
        }}
      /> */}

      <PatientSelectionModal
            open={patientModal}
            onClose={() => setPatientModal(false)}
            onSelect={setSelectedPatient}
            formik={null}
            />

      <ProductTableModal
        show={openProductModal}
        rows={sampleRows}
        onHide={() => setOpenProductModal(false)}
        onSelect={handleSelect}
      />
    </div>
  );
}
